<template>
  <div>
    <Screen
      back-button
      back-button-text="Back"
      :back-button-route="backButtonRoute"
      :header-title="title"
      center-transparent
      right
      :user="user"
      :group-id="selectedGroupId"
    >
      <section
        slot="screen-center"
        class="collection"
      >
        <div
          v-if="showCollectionItems"
          ref="collectionContainer"
          class="collection__items"
        >
          <div
            v-infinite-scroll="handleScrollHitBottom"
            :infinite-scroll-distance="200"
            :infinite-scroll-disabled="hasReachedItemsPaginatedEnd"
          >
            <div
              v-for="collectionGroup in collectionItems"
              :key="`project-${collectionGroup.name}`"
              class="collection__project"
            >
              <div class="collection__project-name">
                {{ collectionGroup.name }}
              </div>
              <div
                :class="[
                  'collection__project-items',
                  { 'collection__project-items-zoom-out-1': zoomLevel === -1 }
                ]"
              >
                <div
                  v-for="(item, itemIndex) in collectionGroup.items"
                  :key="`item-${getStyleId(item)}-${itemIndex}`"
                >
                  <div
                    class="collection__project-item"
                    @click="
                      showStyleDetails(
                        collectionGroup.projectId,
                        getStyleId(item)
                      )
                    "
                  >
                    <StyleOptionPreview
                      v-if="displayOptions"
                      :option-id="item.id"
                      :style-id="getStyleId(item)"
                    />
                    <StylePreview
                      v-else
                      rounded
                      :style-id="getStyleId(item)"
                      :image-resolution="imageResolution"
                    />
                  </div>

                  <StitchDialog
                    :visible="styleDetailsId === getStyleId(item)"
                    fullscreen
                    append-to-body
                    :show-close="false"
                  >
                    <StyleDetailsNew
                      :style-id="getStyleId(item)"
                      :group-id="selectedGroupId"
                      rating-mode
                      @close="styleDetailsId = null"
                    />
                  </StitchDialog>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          :class="[
            'collection__loader',
            { 'collection__loader--visible': isLoading }
          ]"
        >
          <div
            v-loading="true"
            element-loading-background="transparent"
          />
        </div>

        <div
          v-if="!isLoading && optionsFiltered.length === 0"
          class="collection--no-items"
        >
          There are currently no styles in this season 😅
        </div>
      </section>

      <aside
        slot="screen-right"
        class="collection__right"
      >
        <div
          v-if="isModeMCR"
          class="collection__group"
        >
          <h2 class="group__heading">
            Group
          </h2>
          <ElSelect
            v-model="activeGroup"
            class="group__select"
            @change="updateGroupingOption"
          >
            <ElOption
              v-for="item in groupOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </ElSelect>
        </div>
        <div class="collection__filters">
          <StyleOptionFilters
            :applied-filters="filtersFormatted"
            :search="searchText"
            :display-options="displayOptions"
            @filters-changed="updateOptionsFiltered"
          />
        </div>
        <div class="collection__controls">
          <div class="collection__control">
            Separate options
            <ElSwitch
              :value="displayOptions"
              @change="toggleView"
            />
          </div>
          <div class="collection__control">
            Zoom
            <div class="collection__zoom-toggles">
              <ElButton
                :disabled="zoomLevel === -1"
                size="medium"
                icon="el-icon-minus"
                type="primary"
                plain
                circle
                @click="zoomLevel = -1"
              />
              <ElButton
                :disabled="zoomLevel === 0"
                size="medium"
                icon="el-icon-plus"
                type="primary"
                plain
                circle
                @click="zoomLevel = 0"
              />
            </div>
          </div>
        </div>
      </aside>
    </Screen>
  </div>
</template>

<script>
import debounce from 'debounce'
import { mapGetters, mapActions } from 'vuex'
import Utils from '@/services/utils'
import { Workspace } from '@/mixins/workspace'
import features from '@/constants/features'

import { imageResolutions } from '@/constants/image'

import Screen from '@/components/Screen.vue'
import StyleDetailsNew from '@/components/project/StyleDetailsNew.vue'
import StylePreview from '@/components/project/StylePreview.vue'
import StyleOptionPreview from '@/components/project/StyleOptionPreview.vue'
import StyleOptionFilters from '@/components/project/StyleOptionFilters.vue'

export default {
  name: 'Collection',

  components: {
    StyleOptionFilters,
    Screen,
    StyleDetailsNew,
    StylePreview,
    StyleOptionPreview
  },

  mixins: [Workspace],

  data: () => ({
    activeGroup: null,
    groupOptions: [],
    searchText: '',
    styleDetailsId: null,
    filters: [],
    filterGroups: [],
    title: 'Collection Overview',
    displayOptions: false,
    isLoading: true,
    zoomLevel: 0,
    imageResolution: imageResolutions.STYLE_THUMBNAIL,
    isModeMCR: false,
    showCollectionItems: false
  }),

  computed: {
    ...mapGetters([
      'getStyleById',
      'getStyleMetaFilters',
      'getStyleHubFilters',
      'getOptions',
      'getHasReachedOptionsPaginatedEnd',
      'getHasReachedStylesPaginatedEnd'
    ]),

    /**
     * @returns {object}
     */
    styleMetaFilters () {
      return this.getStyleMetaFilters
    },

    /**
     * @returns {object}
     */
    styleHubFilters () {
      return this.getStyleHubFilters
    },

    /**
     * @returns {object}
     */
    styleFilters () {
      return { ...this.styleMetaFilters, ...this.styleHubFilters }
    },

    /**
     * @returns {boolean}
     */
    hasReachedItemsPaginatedEnd () {
      if (this.displayOptions) {
        return this.getHasReachedOptionsPaginatedEnd
      } else {
        return this.getHasReachedStylesPaginatedEnd
      }
    },

    /**
     * @returns {object}
     */
    collectionItems () {
      if (
        !Object.keys(this.styleFilters).includes(this.activeGroup) &&
        this.isModeMCR
      ) {
        this.initializeActiveGroup()
      }

      const collectionItems = this.optionsFiltered.reduce(
        (itemsGrouped, option) => {
          const projectId = option.projectId || option.project.id
          const item = this.displayOptions
            ? option
            : this.getStyleById(option.style_id)

          const collectionGroupKey = this.getCollectionGroupKey(
            item,
            this.displayOptions,
            this.isModeMCR
          )

          let collectionGroup = itemsGrouped.find(
            group => group.name === collectionGroupKey
          )

          if (!collectionGroup) {
            collectionGroup = {
              projectId,
              name: collectionGroupKey,
              items: []
            }

            itemsGrouped.push(collectionGroup)
          }

          collectionGroup.items = [
            ...new Set(collectionGroup.items.concat(item))
          ]

          return itemsGrouped
        },
        []
      )

      if (collectionItems.length > 1) {
        const index = collectionItems.findIndex(
          option => option.name === 'Other'
        )

        if (index || index === 0) {
          collectionItems.push(collectionItems.splice(index, 1)[0])
        }
      }

      return collectionItems
    },

    /**
     * @returns {object}
     */
    optionsFiltered () {
      return this.getOptions.filter(option => {
        return (
          Utils.isUserCreatorInGroup(this.user, this.selectedGroupId) ||
          (option.project && option.project.visible_to_sales)
        )
      })
    },

    /**
     * @returns {boolean}
     */
    canUserAccessFeatureCollectionOverviewByFashionability () {
      if (!this.user) {
        return false
      }

      return this.$featureFlagsManager.canAccessFeature(
        features.COLLECTION_OVERVIEW_BY_FASHIONABILITY,
        {
          user: this.user
        }
      )
    },

    /**
     * @returns {Array}
     */
    filtersFormatted () {
      const filters = []

      Object.entries(this.filters).forEach(([filterKey, filterValue]) => {
        if (Array.isArray(filterValue)) {
          filterValue.forEach(value => {
            filters.push({ [filterKey]: value })
          })
        } else {
          filters.push({ [filterKey]: filterValue })
        }
      })

      return filters
    },

    /**
     * @returns {string}
     */
    backButtonRoute () {
      const previousRouteName = this.$router.previousRoute.name
      const previousRouteProjectId = this.$router.previousRoute.params.id

      if (previousRouteName === 'Present') {
        return `/projects/${previousRouteProjectId}/present`
      } else if (previousRouteName === 'AllBoards') {
        return `/projects/${previousRouteProjectId}/boards`
      } else {
        return '/projects'
      }
    }
  },

  watch: {
    /**
     */
    styleFilters () {
      this.updateGroupOptions()
    }
  },

  async created () {
    this.isLoading = true

    await this.clearCollectionItems()

    this.checkRouteParameters()

    if (this.canUserAccessFeatureCollectionOverviewByFashionability) {
      this.updateGroupOptions()
      this.isModeMCR = true
    }
  },

  methods: {
    ...mapActions([
      'fetchOptionsPaginated',
      'fetchStylesPaginated',
      'fetchStyle',
      'incrementOptionsPaginatedPage',
      'incrementStylesPaginatedPage',
      'updateSelectedGroupId',
      'updateSelectedSeasonId',
      'resetStylesPaginated',
      'resetOptionsPaginated'
    ]),

    /**
     */
    async fetchCollectionItems () {
      const payload = {
        groupId: this.selectedGroupId,
        seasonId: this.selectedSeasonId,
        filters: this.filters,
        searchQuery: this.searchText.trim()
      }

      if (this.isModeMCR) {
        payload.order = 'MCR'
      }

      this.isLoading = true

      if (!this.hasReachedItemsPaginatedEnd) {
        if (this.displayOptions) {
          await this.fetchOptionsPaginated(payload)
        } else {
          await this.fetchStylesPaginated(payload)
        }
      }

      this.isLoading = false

      if (this.$refs.collectionContainer) {
        this.$refs.collectionContainer.dispatchEvent(new CustomEvent('scroll'))
      }
    },

    handleScrollHitBottom: debounce(
      /**
       *
       */
      function () {
        if (this.displayOptions) {
          this.incrementOptionsPaginatedPage()
        } else {
          this.incrementStylesPaginatedPage()
        }

        this.fetchCollectionItems()
      }, 200),

    /**
     * @param   {object}        item
     * @param   {boolean}       isItemOption
     * @param   {boolean}       isModeMCR
     *
     * @returns {string | null}
     */
    getCollectionGroupKey (item, isItemOption, isModeMCR) {
      if (isModeMCR) {
        const defaultOption = 'Other'
        const option = isItemOption
          ? this.getStyleGroup(item.style, this.activeGroup)
          : this.getStyleGroup(item, this.activeGroup)

        return option || defaultOption
      } else {
        return item.project.name
      }
    },

    /**
     * @param   {object} style
     * @param   {string} groupOption
     *
     * @returns {string}
     */
    getStyleGroup (style, groupOption) {
      if (!style) {
        return null
      }

      // removes "Other" from the actual position and add it to the end
      return style.attributes && style.attributes[groupOption]
        ? style.attributes[groupOption]
        : style[groupOption.replace('_id', '')]
          ? style[groupOption.replace('_id', '')].name
          : null
    },

    /**
     * @param   {object} item
     *
     * @returns {number}
     */
    getStyleId (item) {
      if (this.displayOptions) {
        return item.style_id
      } else {
        return item.id
      }
    },

    /**
     */
    checkRouteParameters () {
      const {
        group_id: groupId,
        season_id: seasonId,
        display_options: displayOptions,
        grouping,
        search,
        ...filters
      } = this.$route.query

      if (groupId && seasonId) {
        this.updateSelectedGroupId({
          groupId: Number.parseInt(groupId)
        })
        this.updateSelectedSeasonId({
          seasonId: Number.parseInt(seasonId)
        })

        this.displayOptions = this.parseDisplayOptions(displayOptions)
        this.activeGroup = grouping
        this.searchText = search || ''
        this.filters = filters || []
      } else {
        this.$router.push('/')
      }
    },

    /**
     * @param   {string}  [displayOptions]
     *
     * @returns {boolean}
     */
    parseDisplayOptions (displayOptions) {
      return !!(displayOptions && displayOptions !== 'false')
    },

    /**
     * @param {number} projectId
     * @param {number} styleId
     */
    async showStyleDetails (projectId, styleId) {
      const payload = {
        projectId: projectId,
        styleId: styleId
      }

      await this.fetchStyle(payload)

      this.styleDetailsId = styleId
    },

    /**
     * @param {object} payload
     */
    updateOptionsFiltered (payload) {
      this.searchText = payload.searchText
      this.filters = this.parseStyleOptionFilters(payload.filters)

      this.updateCollectionUrl()

      this.resetCollectionItems()
    },

    /**
     * @param {object} option
     */
    updateGroupingOption (option) {
      this.updateCollectionUrl()

      this.resetCollectionItems()
    },

    /**
     * @param   {Array} filtersToParse
     *
     * @returns {Array}
     */
    parseStyleOptionFilters (filtersToParse) {
      return filtersToParse.reduce((map, filter) => {
        const filterKey = Object.keys(filter)[0]

        if (!map[filterKey]) {
          map[filterKey] = filter[filterKey]
        } else {
          if (!Array.isArray(map[filterKey])) {
            map[filterKey] = [map[filterKey]]
          }

          map[filterKey].push(filter[filterKey])
        }

        return map
      }, {})
    },

    /**
     */
    toggleView () {
      this.displayOptions = !this.displayOptions
    },

    /**
     */
    updateCollectionUrl () {
      const newQuery = {
        group_id: this.selectedGroupId.toString(),
        season_id: this.selectedSeasonId.toString(),
        grouping: this.activeGroup,
        ...this.filters,
        search: this.searchText,
        display_options: this.displayOptions.toString()
      }

      if (JSON.stringify(newQuery) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({
          query: newQuery
        })
      }
    },

    /**
     */
    async clearCollectionItems () {
      await this.resetStylesPaginated()
      await this.resetOptionsPaginated()
    },

    /**
     */
    async resetCollectionItems () {
      this.showCollectionItems = false

      await this.clearCollectionItems()
      await this.fetchCollectionItems()

      this.showCollectionItems = true
    },

    /**
     */
    initializeActiveGroup () {
      // if it's not set or the activeGroup value isn't on the groupOptions (because it has changed)
      if (!this.activeGroup || !this.groupOptions.find((group) => group.value === this.activeGroup)) {
        this.activeGroup = this.groupOptions[0] ? this.groupOptions[0].value : null
      }
    },

    /**
     */
    updateGroupOptions () {
      const filters = Object.entries(this.styleFilters)
      this.groupOptions = filters.map(([key, option]) => {
        return { label: option.name, value: key }
      })

      this.initializeActiveGroup()
    }
  }
}
</script>

<style lang="scss" scoped>
$zoom-out-1-factor: 0.6;
$collection-grid-size: $min-width-aside-large * $zoom-out-1-factor;
$collection-grid-spacing: $gutter * $zoom-out-1-factor;
$height-filters: 80%;
$height-loader: spacing(6);
$group-padding-bottom: spacing(2);

.collection {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.collection__items {
  @include scroll-y-smooth();
}

.collection__right {
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  justify-content: space-between;
  height: 100%;
}

.collection__filters {
  display: flex;
  margin-bottom: $spacer-unit-x2;
  overflow-y: scroll;
}

.collection__view-toggle {
  justify-content: space-between;
  margin-bottom: $spacer-unit-x2;
}

.collection__control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $spacer-unit-x2;

  &:last-of-type {
    margin-bottom: 0;
  }

  .collection__zoom-toggles {
    display: flex;
  }
}

.collection__loading {
  @include size(100%);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.collection__project {
  margin-bottom: $spacer-unit-x4;

  &:last-of-type {
    margin-bottom: $spacer-unit-x2;
  }
}

.collection__project-name {
  margin-bottom: $spacer-unit-x2;
  color: $grey;
  font-size: $font-size-extra-large;
}

.collection__project-items {
  display: grid;
  grid-auto-rows: $min-width-aside-large;
  grid-gap: $gutter;
  grid-template-columns: repeat(auto-fit, $min-width-aside-large);
  width: 100%;

  .collection__project-item {
    @include size(100%);

    cursor: pointer;
    user-select: none;
  }
}

.collection__project-items-zoom-out-1 {
  grid-auto-rows: $collection-grid-size;
  grid-gap: $collection-grid-spacing;
  grid-template-columns:
    repeat(
      auto-fit,
      $collection-grid-size
    );

  .collection__project-item {
    width: $collection-grid-size;
    height: $collection-grid-size;
    margin-right: $collection-grid-spacing;
    margin-bottom: $collection-grid-spacing;
  }

  /deep/ .style-option-preview__name,
  /deep/ .style-preview__name {
    margin-bottom: 0;
    font-size: $font-size-medium;
  }

  /deep/ .style-option-preview__tags,
  /deep/ .style-preview__tags {
    font-size: $font-size-small;
  }
}

.collection__loader {
  height: $height-loader;
  opacity: 0;

  > div {
    height: 100%;
  }

  &.collection__loader--visible {
    opacity: 1;
  }
}

.collection--no-items {
  @include absolute-center;
}

.group__heading {
  text-align: left;
}

.group__heading,
.group__select {
  padding-bottom: $group-padding-bottom;
}
</style>
